// @flow

/**
 * Complète une chaîne jusqu'à une taille donnée.
 * Exemple : pad(7, 3) // '007'
 *
 * @param input La chaîne d'entrée.
 * @param length Le nombre de caractères à atteindre dans la chaîne finale (2 par défaut)
 * @param padString La chaîne de complétion.
 * @return La chaîne complétée.
 */
const pad = function (input: number | string, length: number, padString: ?string) {
  const len = length !== null && length !== undefined ? length : 2;
  let inputStr = input.toString();
  let missing = len - inputStr.length;

  if (missing <= 0) {
    return inputStr;
  }

  let aChar = (padString || '0').charAt(0);
  let prefix = '';

  for (let i = 0; i < missing; ++i) {
    prefix += aChar;
  }
  return prefix + inputStr;
};

function toFixed(value: number, precision: ?number): string {
  let power = 0;

  if (precision !== null && precision !== undefined) {
    precision = Math.abs(undefined !== precision ? precision : 2);
  } else {
    precision = 2;
  }

  power = Math.pow(10, precision);
  return (Math.round(value * power) / power).toFixed(precision);
}

function formatNumber(
  value: number,
  decimal_separator: string,
  thousand_separator: string,
  group_length: number,
  precision: ?number,
  abbreviate: boolean
): string {
  let result = [];
  let str = '';
  let intNum = '';
  let intLength = 0;
  let floatNum = '';
  let floatLength = 0;
  let i = 0;
  let pos = -1;

  if (undefined === precision || precision === null) {
    precision = 2;
  }

  // Pour abréger, on préfère tronquer plutôt qu'arrondir
  if (abbreviate) {
    str = '' + value;
  } else {
    str = toFixed(value, precision);
  }

  pos = str.indexOf('.');
  if (pos !== -1) {
    intNum = str.substring(0, pos);
    floatNum = str.substring(pos + 1);

    if (Math.floor(parseFloat(floatNum)) === 0) {
      floatNum = '';
    } else {
      floatNum += pad('', precision - floatNum.length, '0'); // padRight
    }

    intLength = intNum.length;
    floatLength = abbreviate ? group_length - intLength : precision;

    if (floatLength) {
      floatNum = floatNum.substr(0, floatLength);
    } else {
      floatNum = '';
    }
  } else {
    intNum = str;
    floatNum = '';

    intLength = str.length;
  }

  intNum = pad(intNum, intLength + (group_length - (intLength % group_length)), ' ');

  for (let max = intNum.length; i < max; i += group_length) {
    if (i > 0) {
      result.push(thousand_separator);
    }
    result.push(intNum.substr(i, group_length));
  }

  if (floatNum) {
    result.push(decimal_separator, floatNum);
  }
  return result.join('').replace(new RegExp('^[\\s|' + thousand_separator + ']+'), '');
}

/**
 * Converti un timestamp en date.
 *
 * @param value Le timestamp, en secondes, ou une date.
 */
const toDate = function (value: Date | number): Date {
  const d: any = value;

  if (!(d instanceof Date)) {
    return new Date(d * 1000);
  }
  return d;
};

/**
 * CONFIG
 */

let numConfiguration = {
  patterns: {
    standard: ['-n', 'n'],
    amount: ['-n S', 'n S'],
    blinds: 's-b S'
  },
  group: {
    separator: ' ',
    length: 3,
    abbr: ['', 'k', 'M', 'Md']
  },
  decimal: {
    separator: ',',
    length: 2
  },
  ordinal(number) {
    return number + (number === 1 ? 'er' : 'e');
  }
};

let calendarConfiguration: Object = {
  months: {
    full: [
      'janvier',
      'février',
      'mars',
      'avril',
      'mai',
      'juin',
      'juillet',
      'août',
      'septembre',
      'octobre',
      'novembre',
      'décembre'
    ],
    abbr: ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.']
  },
  days: {
    full: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
    abbr: ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.']
  },
  patterns: {
    longtime: 'HH:mm:ss',
    birthdate: 'dd/MM/yyyy',
    shorttime: 'HH:mm',
    shortdate: 'dd/MM HH:mm',
    shortday: 'dd MMM'
  }
};

/**
 * FORMATTER
 */

const Formatter: any = {};

Formatter.formatNumber = function (value: number, currency: ?string, decimalLength: ?number): string {
  const group = numConfiguration.group;
  const decimal = numConfiguration.decimal;
  const decimalLen = decimalLength === undefined ? decimal.length : decimalLength;
  const patterns = numConfiguration.patterns;
  const result = formatNumber(Math.abs(value), decimal.separator, group.separator, group.length, decimalLen, false);
  const base = currency ? patterns.amount : patterns.standard;
  const pattern = value < 0 ? base[0] : base[1];

  return pattern.replace('S', currency || '').replace('n', result);
};

Formatter.formatOrdinal = function (value: number): string {
  return value + (value === 1 ? 'er' : 'e');
};

Formatter.getPattern = function (name: string): ?string {
  const patterns = calendarConfiguration.patterns;

  if (patterns && patterns[name]) {
    return patterns[name];
  }
  return null;
};

Formatter.formatDate = function (value: Date | number, pattern: string): string {
  const date = toDate(value);
  const patterns = calendarConfiguration.patterns;
  const days = calendarConfiguration.days;
  const months = calendarConfiguration.months;
  const dayOfWeek = date.getDay();
  const day = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();
  const hour = date.getHours();
  const minute = date.getMinutes();
  const second = date.getSeconds();

  let result = [];
  const tokens = /dddd|ddd|dd|d|MMMM|MMM|MM|M|yyyy|mm|m|ss|s|hh|h|HH|H|tt/g;
  let token = '';
  let prev = '';
  let next = '';
  let literal = '';
  let lastIndex = 0;
  let nextApos = 0;
  let part = '';

  // "pattern nommé" ?
  if (patterns[pattern]) {
    pattern = patterns[pattern];
  }

  while (true) {
    lastIndex = tokens.lastIndex;
    token = tokens.exec(pattern);

    if (!token) {
      if (lastIndex !== pattern.length) {
        // Il reste des caractères ne contenant pas de token à la fin
        result.push(pattern.substring(lastIndex));
      }
      break;
    }

    prev = pattern.substring(lastIndex, token.index);

    if (prev) {
      next = pattern.substring(token.index + token[0].length);
      // S'il y a un nombre impair d'apostrophe avant le token, et au moins une après
      if (prev.split("'").length - (1 % 2) && (nextApos = next.indexOf("'")) !== -1) {
        // Nous sommes dans un litéral
        literal = prev.substring(prev.lastIndexOf("'") + 1) + token[0];
        if (nextApos !== 0) {
          literal += next.substring(0, nextApos);
        }
        prev = prev.substring(0, prev.lastIndexOf("'"));
        if (prev !== '') {
          result.push(prev);
        }

        result.push(literal);
        // On manipule la propriété lastIndex afin que la prochaine recherche commence après notre litéral
        tokens.lastIndex += nextApos + 1;

        continue;
      } else {
        result.push(prev);
      }
    }

    part = token[0];

    if (part === 'dddd') {
      result.push(days.full[dayOfWeek]);
    } else if (part === 'ddd') {
      result.push(days.abbr[dayOfWeek]);
    } else if (part === 'dd') {
      result.push(pad(day, 2, '0'));
    } else if (part === 'd') {
      result.push(day);
    } else if (part === 'MMMM') {
      result.push(months.full[month]);
    } else if (part === 'MMM') {
      result.push(months.abbr[month]);
    } else if (part === 'MM') {
      result.push(pad(month + 1, 2, '0'));
    } else if (part === 'M') {
      result.push(month + 1);
    } else if (part === 'yyyy') {
      result.push(year);
    } else if (part === 'HH') {
      result.push(pad(hour, 2, '0'));
    } else if (part === 'H') {
      result.push(hour);
    } else if (part === 'hh') {
      result.push(pad(hour % 12, 2, '0'));
    } else if (part === 'h') {
      result.push(hour % 12);
    } else if (part === 'mm') {
      result.push(pad(minute, 2, '0'));
    } else if (part === 'm') {
      result.push(minute);
    } else if (part === 'ss') {
      result.push(pad(second, 2, '0'));
    } else if (part === 's') {
      result.push(second);
    } else if (part === 'tt') {
      result.push(hour < 13 ? 'am' : 'pm');
    }
  }
  return result.join('');
};

Formatter.setCultureInfo = function (numberConfiguration: Object, dateConfiguration: Object) {
  numConfiguration = numberConfiguration;
  calendarConfiguration = dateConfiguration;
};

export default Formatter;
